import * as React from 'react';

import {LoadingPage} from 'shells/loading-page/loading-page';
import {Modal} from 'shells/modal/modal';
import {LeftFrame} from 'shells/modal/step-modal/layout';
import {StepList} from 'shells/modal/step-modal/step-list';

import {useGetCalendarSync} from '../graphql/hooks/use-get-calendar-sync';

import {CalendarDone} from './calendar-done';
import {CalendarGetStarted} from './calendar-get-started';
import {CalendarSelect} from './calendar-select';
import {CalendarSettings} from './calendar-settings';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

type Step = 'loading' | 'get-started' | 'select' | 'settings' | 'done';

const STEPS = ['1. Get started', '2. Select calendar', '3. Configure settings', "4. You're done!"];

const STEP_INDEX: Partial<Record<Step, number>> = {
    loading: 0,
    'get-started': 0,
    select: 1,
    settings: 2,
    done: 3,
};

export function CalendarConnectModalRoot(props: Props) {
    const [currentStep, setCurrentStep] = React.useState<Step>('loading');

    const {calendarSync, refetch, isLoading} = useGetCalendarSync(!props.isOpen);

    const handleClose = () => {
        setCurrentStep('loading');
        props.onClose();
    };

    React.useEffect(() => {
        if (props.isOpen && currentStep === 'loading' && !isLoading) {
            if (calendarSync && calendarSync.isEnabled) {
                setCurrentStep('done');
            } else if (calendarSync) {
                setCurrentStep('select');
            } else {
                setCurrentStep('get-started');
            }
        }

        if (!props.isOpen) {
            setCurrentStep('loading');
        }
    }, [props.isOpen, currentStep, isLoading, calendarSync]);

    let content = null;
    if (currentStep === 'loading') {
        content = <LoadingPage />;
    } else if (currentStep === 'get-started') {
        content = (
            <CalendarGetStarted
                calendarSync={calendarSync}
                refetch={refetch}
                onCancel={handleClose}
                nextPage={() => setCurrentStep('select')}
            />
        );
    } else if (currentStep === 'select') {
        content = <CalendarSelect nextPage={() => setCurrentStep('settings')} />;
    } else if (currentStep === 'settings') {
        content = (
            <CalendarSettings
                goBack={() => setCurrentStep('select')}
                nextPage={() => setCurrentStep('done')}
            />
        );
    } else if (currentStep === 'done') {
        content = <CalendarDone onClose={handleClose} />;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={handleClose}
            canClickOutsideToClose={false}
            canEscapeToClose={false}
            maxHeight={600}
            noPadding={true}
        >
            <div className='flex full-height'>
                <LeftFrame title='Calendar sync'>
                    {/* @ts-expect-error */}
                    <StepList activeStepIndex={STEP_INDEX[currentStep]} steps={STEPS} />
                </LeftFrame>
                {content}
            </div>
        </Modal>
    );
}
